import { Button, makeStyles } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useRouterStore } from 'mobx-state-router';
import React, { useContext, useEffect, useState } from 'react';

import { generateRouteUrl } from 'src/boot/routes';
import { Colors } from 'src/common/ui/base/Colors';
import Snackbar from 'src/components/general/Snackbar';
import { SideVisitContext } from 'src/components/pages/eventShow/SideVisitContext';
import { isInactiveStatus } from 'src/dropInClinic/activeStatuses';
import { useGetPicRequest } from 'src/dropInClinic/hooks/useGetPicRequest';
import { ACCEPT_MUTATION } from 'src/dropInClinic/queries';
import { AcceptMutationArgs, AcceptMutationResponse } from 'src/dropInClinic/types';
import { InlineHelp } from 'src/nightingale/components/ChartElement/InlineHelp';
import useApolloClient from 'src/shared/client/useApolloClient';
import useRootStore from 'src/stores/useRootStore';

export const AcceptPopInRequestFromUrl = () => {
  const apolloClient = useApolloClient();
  const { setSideVisit } = useContext(SideVisitContext);

  const router = useRouterStore();
  const {
    auth: { user: currentUser },
  } = useRootStore();
  const requestId = router.routerState.params.id;
  const {
    data,
    error: getRequestError,
    isLoading: isLoadingRequest,
    mutate: mutateGetRequest,
  } = useGetPicRequest(requestId);
  const request = data?.staff_getDropInClinicRequest;

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const classes = useStyles();

  useEffect(() => {
    if (getRequestError) {
      setError(`Error getting request with ID ${requestId}`);
    } else if (!request && !isLoadingRequest) {
      setError(`No request found with ID ${requestId}`);
    }
  }, [request, isLoadingRequest, getRequestError, requestId]);

  const isRequestUnassigned = !request?.providerId;
  const isCurrentUserRequestProvider = request?.providerId === currentUser?.id;
  const isInactiveRequest = request?.status && isInactiveStatus(request.status);
  const enableAccept = !isInactiveRequest && isRequestUnassigned;

  const getHelperText = () => {
    if (isInactiveRequest) {
      return `This request has an inactive status, ${request?.status}, and cannot be accepted.`;
    } else if (isRequestUnassigned) {
      return 'Accepting will assign you to the request.';
    } else if (isCurrentUserRequestProvider) {
      return 'You have already been assigned to this request.';
    } else {
      return 'This request has already been assigned to another provider.';
    }
  };

  const handleViewNote = () => {
    const eventId = request?.eventId;
    if (eventId) {
      router.goTo('showEvent', { params: { id: eventId } });
    } else {
      setError('No event found for this request');
    }
  };

  const handleAcceptRequest = async () => {
    setLoading(true);

    if (!requestId) {
      setError('No request ID provided');
      setLoading(false);
      return;
    }

    if (!apolloClient) {
      setError('Apollo client not available');
      setLoading(false);
      return;
    }

    try {
      const response = await apolloClient.mutate<AcceptMutationResponse, AcceptMutationArgs>({
        mutation: ACCEPT_MUTATION,
        variables: { requestId },
      });

      const eventId = response.data?.acceptDropInClinicRequest?.eventId;
      const patientId = response.data?.acceptDropInClinicRequest?.patientId;
      if (eventId && patientId) {
        // Open side-by-side visit note in new tab, becomes new focused tab
        setSideVisit(patientId, eventId);
        const visitNoteUrl = generateRouteUrl('showPatient', { id: patientId, tab: 'erx' });
        window.open(visitNoteUrl, '_blank');
        // Open video call in current tab
        router.goTo('eventVc', { params: { event: eventId } });
      } else {
        // Refresh the request data to show updated state (most likely request is no longer acceptable)
        mutateGetRequest();
        setLoading(false);
        return;
      }

      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred');
    }

    setLoading(false);
  };

  if (isLoadingRequest) {
    return (
      <div className={classes.root}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div>
          {error && (
            <Snackbar
              message={error}
              onDismiss={() => {
                setError(null);
              }}
              color="error"
            />
          )}
          {success && (
            <Snackbar
              message="Request accepted! Redirecting..."
              onDismiss={() => {
                setSuccess(false);
              }}
            />
          )}
          <section className={classes.section}>
            <span className={classes.titleText}>Accept Pop-In Clinic Request</span>
            {request && (
              <Card className={classes.cardContainer}>
                <section className={classes.section}>
                  <CardHeader
                    title="Pop-In Clinic Request"
                    classes={{ title: classes.header, root: classes.cardHeaderContainer }}
                  />
                </section>

                <CardContent className={classes.cardContentContainer}>
                  <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <InlineHelp helpText={getHelperText()} />
                  </div>
                  <div className={classes.spacer} />
                  {enableAccept && (
                    <Button
                      className={classes.acceptButton}
                      onClick={handleAcceptRequest}
                      disabled={loading || success}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {loading ? 'Accepting...' : 'Accept'}
                    </Button>
                  )}
                  {!enableAccept && (
                    <Button className={classes.acceptButton} onClick={handleViewNote}>
                      View Note
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  acceptButton: {
    backgroundColor: Colors.Stillwater,
    color: 'white',
    '&:hover': {
      backgroundColor: Colors.BlueSpruce,
    },
  },
  spacer: {
    flex: '1 1 100%',
  },
  content: {
    maxWidth: 600,
  },
  header: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '17.4px',
    '&, &.Mui-error, &.Mui-focused': {
      color: Colors.BlueSpruce,
    },
    transform: 'scale(1)',
    position: 'static',
  },
  cardContainer: {
    backgroundColor: Colors.White,
    borderRadius: 0,
    boxShadow: 'none',
    marginTop: 30,
    overflow: 'visible',
    padding: 16,
  },
  cardHeaderContainer: {
    padding: 0,
  },
  cardContentContainer: {
    padding: 0,
    position: 'relative',
    '&:last-child': { paddingBottom: 0 },
  },
  title: {
    flex: '0 0 auto',
    marginBottom: 16,
  },
  section: {
    marginBottom: 20,
  },
  titleText: {
    fontFamily: 'Tenor Sans',
    fontSize: 26,
    lineHeight: '145%',
    color: Colors.Gray6,
    textTransform: 'capitalize',
    height: '50px',
    marginBottom: '30px',
  },
  root: {
    padding: theme.spacing(3),
    height: '100%',
    minHeight: '100vh',
  },
}));
